/**
 * @file Calendar visual settings owner.
 */

import { tempConfigData } from "../../app/state/state.js";
import {
    bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    bindCalendarThresholdDefaultControls,
    CALENDAR_THRESHOLD_ITEM_TYPES,
    getCalendarThresholdDefaultDraft,
    getCalendarThresholdMaxValue,
    normalizeCalendarThresholdDraft,
    renderCalendarThresholdDefaultControl,
    syncCalendarThresholdDefaultControl,
} from "../calendar/notificationThresholdControls.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";

const DEFAULT_CALENDAR_SETTINGS = Object.freeze({
    eventColor: "#DBEAFE",
    reminderColor: "#F78183",
    leaveColor: "#DCFCE7",
    providerLeaveColor: "#BBF7D0",
    nonBusinessDayColor: "#F3F4F6",
    holidayColor: "#FEF3C7",
});
const COLOUR_CONTROLS = Object.freeze([
    {
        key: "eventColor",
        label: "Event colour",
        description: "Used for standard shared calendar events.",
    },
    {
        key: "leaveColor",
        label: "Leave colour",
        description: "Used for approved leave for non-provider users.",
    },
    {
        key: "providerLeaveColor",
        label: "Provider leave colour",
        description: "Used for approved leave when the user is an app provider.",
    },
    {
        key: "reminderColor",
        label: "Reminder colour",
        description: "Used for shared calendar reminder items.",
    },
    {
        key: "nonBusinessDayColor",
        label: "Non-business-day colour",
        description: "Used for dates outside the configured Business Calendar.",
    },
    {
        key: "holidayColor",
        label: "Holiday colour",
        description: "Used for configured Business Calendar holiday dates.",
    },
]);
const THRESHOLD_DEFAULT_CONTROLS = Object.freeze([
    {
        itemType: CALENDAR_THRESHOLD_ITEM_TYPES.EVENT,
        label: "Event default",
        description: "Copied to new timed events. All-day events use days.",
    },
    {
        itemType: CALENDAR_THRESHOLD_ITEM_TYPES.REMINDER,
        label: "Reminder default",
        description: "Copied to new calendar reminders.",
    },
    {
        itemType: CALENDAR_THRESHOLD_ITEM_TYPES.LEAVE,
        label: "Leave default",
        description: "Copied to new leave reminder settings. Days only.",
    },
]);

let updateSaveButtonHandler = () => {};

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function isHexColor(value) {
    return /^#[0-9a-f]{6}$/i.test(String(value || "").trim());
}

function ensureCalendarConfig() {
    if (!tempConfigData.calendar || typeof tempConfigData.calendar !== "object") {
        tempConfigData.calendar = {};
    }
    tempConfigData.calendar = {
        ...DEFAULT_CALENDAR_SETTINGS,
        ...tempConfigData.calendar,
    };
    delete tempConfigData.calendar.defaultEventReminderLeadDays;
    return tempConfigData.calendar;
}

function ensureCalendarThresholdDefaults() {
    if (!tempConfigData.calendarThresholdDefaults ||
        typeof tempConfigData.calendarThresholdDefaults !== "object") {
        tempConfigData.calendarThresholdDefaults = {};
    }
    return tempConfigData.calendarThresholdDefaults;
}

function renderColourControl(control, calendarConfig) {
    const value = attrValue(calendarConfig[control.key]);
    const inputId = `calendar-${attrValue(control.key)}-picker`;
    return `
        <div class="app-colour-row" data-calendar-colour-pair="${attrValue(control.key)}">
            <label class="app-colour-swatch-control" for="${inputId}">
                <span
                    class="app-colour-swatch"
                    style="background-color: ${value}"
                    data-calendar-colour-swatch="${attrValue(control.key)}"
                    aria-hidden="true"
                ></span>
                <input
                    id="${inputId}"
                    type="color"
                    class="app-colour-picker"
                    value="${value}"
                    aria-label="Pick ${escapeHTML(control.label)}"
                    data-calendar-setting="${attrValue(control.key)}"
                    data-calendar-colour-role="picker"
                >
            </label>
            <div class="app-colour-copy">
                <div class="app-colour-label">${escapeHTML(control.label)}</div>
                <p class="app-colour-description">${escapeHTML(control.description)}</p>
            </div>
            <input
                type="text"
                class="app-colour-hex-input"
                value="${value}"
                maxlength="7"
                placeholder="#RRGGBB"
                spellcheck="false"
                data-calendar-setting="${attrValue(control.key)}"
                data-calendar-colour-role="text"
                aria-label="${escapeHTML(control.label)} hex value"
            >
        </div>
    `;
}

function renderThresholdDefaultsSection() {
    const defaults = ensureCalendarThresholdDefaults();
    return `
        <section class="bg-white rounded-lg shadow-md p-6 space-y-5">
            <div>
                <h3 class="text-lg font-semibold text-gray-900">Calendar notification defaults</h3>
                <p class="mt-1 text-sm text-gray-500">
                    Defaults are copied to new Calendar items. Existing items keep their own reminder settings.
                </p>
            </div>
            <div class="grid gap-4">
                ${THRESHOLD_DEFAULT_CONTROLS.map((control) =>
        renderCalendarThresholdDefaultControl({
            ...control,
            threshold: defaults[control.itemType] || null,
            namePrefix: "settings-calendar-threshold",
        })).join("")}
            </div>
        </section>
    `;
}

/**
 * Renders the admin Calendar settings tab.
 *
 * @param {HTMLElement} container Settings tab container.
 * @param {object} options Render options.
 * @param {Function} options.updateSaveButton Dirty-state callback.
 * @returns {void}
 */
export function renderCalendarSettings(container, { updateSaveButton } = {}) {
    updateSaveButtonHandler = updateSaveButton || (() => {});
    const calendarConfig = ensureCalendarConfig();
    const wrapperClass = "space-y-6";
    container.innerHTML = `
        <div class="${wrapperClass}">
            <section class="bg-white rounded-lg shadow-md p-6 space-y-5">
                <div>
                    <h3 class="text-lg font-semibold text-gray-900">Calendar settings</h3>
                    <p class="mt-1 text-sm text-gray-500">
                        Configure shared calendar colours.
                    </p>
                </div>
                <div class="app-colours-list">
                    ${COLOUR_CONTROLS.map((control) =>
        renderColourControl(control, calendarConfig)).join("")}
                </div>
            </section>
            ${renderThresholdDefaultsSection()}
        </div>
    `;
    bindNativeFieldBehavior(container);
    bindCalendarThresholdDefaultControls(container, (row) => {
        readThresholdDefaultRow(row);
        updateSaveButtonHandler(true);
    });
}

function updateThresholdDefaultDom(row, itemType) {
    const defaults = ensureCalendarThresholdDefaults();
    const draft = getCalendarThresholdDefaultDraft(defaults[itemType], {
        itemType,
    });
    const enabled = Boolean(defaults[itemType]);
    const valueInput = row.querySelector(
        '[data-calendar-threshold-field="value"]',
    );
    const unitRoot = row.querySelector(
        "[data-calendar-threshold-default-unit-control]",
    );
    const clearButton = row.querySelector("[data-calendar-threshold-clear]");

    if (valueInput) {
        valueInput.disabled = !enabled;
        valueInput.max = String(getCalendarThresholdMaxValue(draft.unit));
        if (!valueInput.value) {
            valueInput.value = String(draft.value || 1);
        }
    }
    if (unitRoot) {
        unitRoot.dataset.calendarThresholdUnit = draft.unit;
    }
    if (clearButton) {
        clearButton.disabled = !enabled;
    }
    syncCalendarThresholdDefaultControl(row, {
        itemType,
        enabled,
        unit: draft.unit,
    });
}

function readThresholdDefaultRow(row) {
    const itemType = row?.dataset?.calendarThresholdDefault;
    if (!itemType) {return false;}
    const enabled = row.querySelector(
        '[data-calendar-threshold-field="enabled"]',
    )?.checked === true;
    const defaults = ensureCalendarThresholdDefaults();

    if (!enabled) {
        defaults[itemType] = null;
        updateThresholdDefaultDom(row, itemType);
        return true;
    }

    const value = Number(row.querySelector(
        '[data-calendar-threshold-field="value"]',
    )?.value || 1);
    const unit = row.querySelector(
        "[data-calendar-threshold-default-unit-control]",
    )?.dataset.calendarThresholdUnit || "days";
    defaults[itemType] = {
        ...defaults[itemType],
        unit,
        value,
        enabled: true,
    };
    updateThresholdDefaultDom(row, itemType);
    return true;
}

/**
 * Applies Calendar settings input changes.
 *
 * @param {HTMLElement} target Event target.
 * @returns {boolean} True when handled.
 */
export function handleCalendarSettingsChange(target) {
    const thresholdRow = target?.closest?.("[data-calendar-threshold-default]");
    if (thresholdRow && target.dataset?.calendarThresholdField) {
        readThresholdDefaultRow(thresholdRow);
        updateSaveButtonHandler(true);
        return true;
    }

    const key = target?.dataset?.calendarSetting;
    if (!key) {return false;}

    const calendarConfig = ensureCalendarConfig();
    calendarConfig[key] = String(target.value || "").trim();
    const pair = target.closest("[data-calendar-colour-pair]");
    const linked = pair?.querySelectorAll(
        `[data-calendar-setting="${CSS.escape(key)}"]`,
    ) || [];
    linked.forEach((control) => {
        if (control !== target && isHexColor(calendarConfig[key])) {
            control.value = calendarConfig[key];
        }
    });
    const swatch = pair?.querySelector("[data-calendar-colour-swatch]");
    if (swatch && isHexColor(calendarConfig[key])) {
        swatch.style.backgroundColor = calendarConfig[key];
    }

    updateSaveButtonHandler(true);
    return true;
}

function handleThresholdDefaultClick(target) {
    const row = target?.closest?.("[data-calendar-threshold-default]");
    if (!row || !target.closest("[data-calendar-threshold-clear]")) {
        return false;
    }
    const checkbox = row.querySelector(
        '[data-calendar-threshold-field="enabled"]',
    );
    if (checkbox) {
        checkbox.checked = false;
    }
    readThresholdDefaultRow(row);
    updateSaveButtonHandler(true);
    return true;
}

/**
 * Handles Calendar settings buttons.
 *
 * @param {HTMLElement} target Event target.
 * @returns {boolean} True when handled.
 */
export function handleCalendarSettingsButtonClick(target) {
    if (handleThresholdDefaultClick(target)) {return true;}
    if (!target?.closest?.("[data-calendar-colour-pair]")) {return false;}
    return true;
}

/**
 * Validates the Calendar settings draft before save.
 *
 * @returns {boolean} True when valid.
 */
export function validateCalendarSettingsDraft() {
    const calendarConfig = ensureCalendarConfig();
    const invalidColour = COLOUR_CONTROLS.find((control) =>
        !isHexColor(calendarConfig[control.key]));
    if (invalidColour) {
        openConfirmationModal(
            "Calendar Settings",
            `${escapeHTML(invalidColour.label)} must be a six-digit hex colour.`,
            () => {},
            false,
            "OK",
        );
        return false;
    }

    const defaults = ensureCalendarThresholdDefaults();
    const invalidDefault = THRESHOLD_DEFAULT_CONTROLS.find((control) => {
        if (!defaults[control.itemType]) {return false;}
        const normalized = normalizeCalendarThresholdDraft(
            defaults[control.itemType],
            { itemType: control.itemType },
        );
        if (!normalized) {return true;}
        defaults[control.itemType] = normalized;
        return false;
    });
    if (invalidDefault) {
        openConfirmationModal(
            "Calendar Settings",
            `${escapeHTML(invalidDefault.label)} must use a supported whole-number lead time.`,
            () => {},
            false,
            "OK",
        );
        return false;
    }

    return true;
}
